// TEMPORARY (NVCMP): product info comparison test page — remove when COBOL product info is retired.
import axios, { AxiosRequestConfig } from 'axios';
import U from './Utils';
import CSharpUtils from './CSharpUtils';

/** Vare i svaret fra gammel (COBOL) og ny (/newinfo) vareinfo — samme form for begge. */
export interface CompareProduct {
  alpha?: string | null;
  productNumber?: string | null;
  altAlpha?: string | null;
  altProductNumber?: string | null;
  productDescription?: string | null;
  stock?: number | null;
  price?: number | null;
  surcharge?: number | null;
  sellingPrice?: number | null;
  bestInfo?: string | null;
  note?: string | null;
  surchargeCode?: string | null;
  surchargeType?: string | null;
  discountInfo?: string | null;
  cartInholdPerPackage?: number | null;
  edbNumber?: string | null;
  recommendedPrice?: number | null;
  location?: string | null;
  discount1?: number | null;
  discount2?: number | null;
  discount3?: number | null;
  text?: string | null;
  ean?: string | null;
  orderPrice?: number | null;
  orderFullCost?: number | null;
  totalStock?: number | null;
  priceADKasse?: number | null;
  tireMarkingInfo?: string | null;
  customs?: string | null;
  orderDeliveryTime?: string | null;
  warehouses?: {
    stock?: number | null;
    warehouse?: string | null;
    warehouseCode?: string | null;
    /** Bare fra ny: firmaet som har lageret (eget firma, eller leverandøren for et eksternt lager). */
    companyId?: string | null;
    warehouseType?: 'Internal' | 'External' | null;
  }[];
  campaign1?: { campaign?: string | null; price?: number | null };
  campaign2?: { campaign?: string | null; price?: number | null };
}

export interface CompareInvalidProduct {
  alfa?: string | null;
  artikkelnummer?: string | null;
  feilmelding?: string | null;
  productId?: string | null;
}

export interface CompareProductInfosResponse {
  status?: { code?: number; status?: string | null };
  products?: CompareProduct[] | null;
  invalidProducts?: CompareInvalidProduct[] | null;
}

/** Ett avvik slik sammenligningen i backend fant det. field er null for generelle funn. */
export interface CompareDifference {
  edbNumber?: string | null;
  productId?: string | null;
  field?: string | null;
  cobol?: string | null;
  handler?: string | null;
  text: string;
  /** Kjent, akseptert forskjell (f.eks. slettet vare): vises som advarsel, gjør ikke svarene ulike. */
  isWarning?: boolean;
}

export interface ProductInfoCompareResult {
  companyId: string;
  customerNumber: number;
  adk: boolean;
  cobol?: CompareProductInfosResponse | null;
  cobolElapsedMs: number;
  /** Tid per COBOL-kall (25 varer per kall), i rekkefølge. */
  cobolBatchElapsedMs?: number[];
  cobolError?: string | null;
  handler?: CompareProductInfosResponse | null;
  handlerElapsedMs: number;
  handlerError?: string | null;
  areEqual?: boolean | null;
  differences: CompareDifference[];
}

export interface ProductInfoComparePayload {
  /** Tre siffer; tom = innlogget brukers firma. */
  companyId?: string;
  customerNumber: number;
  products: { productId: string; quantity: number }[];
  adk: boolean;
}

/**
 * POST {WEBAPI_PATH}/product/infocompare — kun for brukere som starter med FIR.
 * Siden sender 25 varer per kall (se ProductInfoCompare); COBOL på dev bruker ca. 1,2 s per vare.
 */
export const compareProductInfo = (payload: ProductInfoComparePayload, signal?: AbortSignal) => {
  const config = {
    method: 'post',
    baseURL: `${U.URL_ADDRESS}${U.WEBAPI_PATH}/product/infocompare`,
    timeout: 180000, // 3 min per kall à 25 varer
    signal,
    headers: {
      'content-type': 'application/json',
      Accept: 'application/json;charset=utf-8',
      Authorization: `Bearer ${CSharpUtils.Get_Token}`,
    },
    data: payload,
    responseType: 'json',
  } as AxiosRequestConfig;
  return axios.request<ProductInfoCompareResult>(config);
};

/**
 * Slår sammen svarene fra flere kall (hvert med sine varer) til ett resultat: varer, ugyldige varer og avvik
 * legges etter hverandre, tidene summeres. Feil merkes med delnummeret. Like bare når alle delene er like;
 * ukjent (null) når en del ikke kunne sammenlignes og ingen del har avvik.
 */
export const mergeCompareResults = (parts: ProductInfoCompareResult[]): ProductInfoCompareResult => {
  const side = (pick: (p: ProductInfoCompareResult) => CompareProductInfosResponse | null | undefined) => {
    const responses = parts.map(pick).filter((r): r is CompareProductInfosResponse => !!r);
    return responses.length === 0
      ? null
      : {
          status: responses[0].status,
          products: responses.flatMap(r => r.products ?? []),
          invalidProducts: responses.flatMap(r => r.invalidProducts ?? []),
        };
  };
  const errors = (pick: (p: ProductInfoCompareResult) => string | null | undefined) =>
    parts
      .map((p, i) => (pick(p) ? (parts.length > 1 ? `Del ${i + 1}: ${pick(p)}` : `${pick(p)}`) : null))
      .filter(Boolean)
      .join(' · ') || null;
  const sum = (pick: (p: ProductInfoCompareResult) => number) => parts.reduce((n, p) => n + (pick(p) ?? 0), 0);

  return {
    companyId: parts[0].companyId,
    customerNumber: parts[0].customerNumber,
    adk: parts[0].adk,
    cobol: side(p => p.cobol),
    cobolElapsedMs: sum(p => p.cobolElapsedMs),
    cobolBatchElapsedMs: parts.flatMap(p => (p.cobolBatchElapsedMs?.length ? p.cobolBatchElapsedMs : [p.cobolElapsedMs])),
    cobolError: errors(p => p.cobolError),
    handler: side(p => p.handler),
    handlerElapsedMs: sum(p => p.handlerElapsedMs),
    handlerError: errors(p => p.handlerError),
    areEqual: parts.some(p => p.areEqual === false) ? false : parts.every(p => p.areEqual === true) ? true : null,
    differences: parts.flatMap(p => p.differences ?? []),
  };
};
